import { Company } from '../../../types/company/Company'

/**
 * Recursively flattens a company group tree (a company plus its `subsidiaries`, arbitrarily
 * nested, including subsidiaries that are themselves subgroups with their own subsidiaries)
 * into a flat array of the underlying operating companies.
 *
 * A subgroup wrapper company (one that has a `.group` field pointing to its own base company
 * record — same real company, e.g. a different `accountingSoftware`/`contract`) is excluded
 * from the result and replaced by that base record. Use this when you need a flat list of
 * selectable/real companies, e.g. for a company picker or a per-company table — not when the
 * subgroup wrapper itself also needs to be selectable, see {@link flattenGroupWithSubgroups}.
 */
export const flattenGroup = (company: Company): Company[] => {
  const baseCompanies = [company, company.group].filter(Boolean) as Company[]
  const allSubsidiaries = baseCompanies.slice()

  company.subsidiaries?.forEach((subsidiary: Company) => {
    const flattenedSubsidiaries = flattenGroup(subsidiary)
    for (const sub of flattenedSubsidiaries) {
      allSubsidiaries.push(sub)
    }
  })

  return allSubsidiaries.filter(({ group }) => !group)
}

/**
 * Recursively flattens a company group tree (a company plus its `subsidiaries`, arbitrarily
 * nested, including subsidiaries that are themselves subgroups with their own subsidiaries)
 * into a flat array of companies.
 *
 * Unlike {@link flattenGroup}, a subgroup wrapper company found among the subsidiaries is kept
 * in the result alongside its base company record — both appear as separate entries. Use this
 * when a subgroup wrapper itself needs to be selectable (e.g. a formula's company argument),
 * not when you only want the underlying operating companies.
 *
 * The top-level `company` argument itself is never included as an entry — callers typically
 * pass the `CompanyGroup` envelope (`{ group, subsidiaries }`) here, not a real company, so only
 * its `.group` (the real top company) and its subsidiaries are flattened.
 */
export const flattenGroupWithSubgroups = (company: Company): Company[] => {
  const flatten = (current: Company, includeSelf: boolean): Company[] => {
    const baseCompanies = [includeSelf ? current : undefined, current.group].filter(Boolean) as Company[]
    const allCompanies = baseCompanies.slice()

    current.subsidiaries?.forEach((subsidiary: Company) => {
      allCompanies.push(...flatten(subsidiary, true))
    })

    return allCompanies
  }

  return flatten(company, false)
}
